// 2026-05-26 - FormuLearning - Step 3 §3.3 Y 변수 표
// 2026-09-22 수정 - 이슈 #67 S4-2: 용출액별 f2 파생 행(프로세스 v1.14 `BR-RND-05` · UI/UX v2.5 §4.6 ③).
//              파생 행은 **서버가 만든다** — 활성 Lot 용출액마다 원 `f2` 행 바로 아래에 읽기 전용으로
//              그린다. Target·Constraint 는 원 행의 **지금 값**(편집 중인 값 포함)을 따라 보인다.
//              편집 대상은 파생 아닌 행뿐이며, 저장하면 서버가 파생 행을 버리고 다시 만든다.
// 2026-09-24 수정 - 이슈 #75(M915-04): 기본 행 시딩은 `onSeed` 로 보낸다 — 주성분 확정 확인(BR-STEP-03)은
//              사용자 저장에만 걸려야 한다. 시딩까지 태우면 빈 프로젝트를 열기만 해도 확인창이 뜬다.
// 2026-09-26 수정 - 이슈 #97: `readOnly`(BR-STEP-01, Step 4 진입 뒤)면 기본 행 시딩을 하지 않는다 — 시딩은
//              자동 쓰기라 읽기 전용 카드를 연 것만으로 Y 가 저장되고 분석 계기까지 등록된다.
// 파일 위치: frontend/src/features/step3/YVariableSection.tsx

import { Fragment, useEffect, useRef, useState } from 'react'
import type { YVariable, YTarget } from '../../api/step3'

interface Props {
  variables: YVariable[]
  onChange: (vars: YVariable[]) => void
  /** 기본 행 시딩 저장(#75) — 사용자 저장이 아니므로 주성분 확정 확인을 거치지 않는 경로. 없으면 onChange. */
  onSeed?: (vars: YVariable[]) => void
  /** 읽기 전용(#97, BR-STEP-01) — 기본 행 시딩을 하지 않는다. 입력 비활성은 바깥 fieldset 이 맡는다. */
  readOnly?: boolean
}

const TARGETS: { value: YTarget; label: string }[] = [
  { value: 'maximize', label: 'Maximize' },
  { value: 'minimize', label: 'Minimize' },
  { value: 'target', label: 'Target' },
]

// Target에 따른 Constraint 표시 기호. maximize → ≥, minimize → ≤, target → =
const SYMBOL: Record<YTarget, string> = { maximize: '≥', minimize: '≤', target: '=' }

// 기본 응답행(이슈 #28). f2는 기본 제약 50(≥), Hardness는 비워둔다.
const DEFAULT_ROWS: YVariable[] = [
  { response: 'f2', target: 'maximize', constraints: '50', is_selected: true, target_value: null },
  { response: 'Hardness (kp)', target: 'target', constraints: '', is_selected: true, target_value: null },
]

const emptyRow: YVariable = { response: '', target: 'maximize', constraints: '', is_selected: true, target_value: null }

/** 원 f2 행 — 백엔드 `y_derive.is_origin_f2()` 와 같은 규약(파생 아님 · 이름이 정확히 `f2`). */
const isOriginF2 = (v: YVariable) => !v.is_derived && (v.response ?? '').trim().toLowerCase() === 'f2'

export default function YVariableSection({ variables, onChange, onSeed, readOnly = false }: Props) {
  // 편집 대상 = 파생 아닌 행. 파생 행은 props(서버 응답)에서 받아 **표시만** 한다.
  const [rows, setRows] = useState<YVariable[]>(() => variables.filter((v) => !v.is_derived))
  const derived = variables.filter((v) => v.is_derived)
  const seededRef = useRef(false)

  // 신규 프로젝트(빈 y_variables) 최초 진입 시 기본 응답행을 시딩한다. 한 번만 수행하며,
  // 이후 사용자가 모두 삭제하더라도 재시딩하지 않는다. 읽기 전용이면 하지 않는다(#97).
  useEffect(() => {
    if (readOnly) return
    if (!seededRef.current && variables.length === 0) {
      seededRef.current = true
      setRows(DEFAULT_ROWS)
      ;(onSeed ?? onChange)(DEFAULT_ROWS) // 화면이 넣는 기본값 — 사용자 저장이 아니다(#75 BR-STEP-03)
    }
  }, [variables, onChange, onSeed, readOnly])

  // 편집 행과 함께 지금의 파생 행을 넘긴다 — Autosave OFF 의 낙관적 반영에서도 파생 행이
  // 화면에 남게. 서버는 받은 파생 행을 버리고 활성 Lot 기준으로 다시 만든다.
  const emit = (next: YVariable[]) => {
    setRows(next)
    onChange([...next, ...derived])
  }

  const update = (idx: number, patch: Partial<YVariable>) => {
    const next = [...rows]
    next[idx] = { ...next[idx], ...patch }
    emit(next)
  }

  const addRow = () => emit([...rows, { ...emptyRow }])

  const removeRow = (idx: number) => emit(rows.filter((_, i) => i !== idx))

  const originIdx = rows.findIndex(isOriginF2)

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
          Y Variables (Responses)
        </h4>
        <button
          onClick={addRow}
          className="rounded px-2 py-1 text-[10px] text-text-secondary transition-colors hover:bg-bg-input"
          style={{ border: '1px solid var(--border-soft)' }}
        >
          + Response
        </button>
      </div>
      <div className="overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
        <table className="w-full text-[11px]">
          <thead>
            <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
              <th className="px-2 py-1.5 text-left font-medium text-text-secondary">Response</th>
              <th className="px-2 py-1.5 text-center font-medium text-text-secondary">Target</th>
              <th className="px-2 py-1.5 text-left font-medium text-text-secondary">Constraint</th>
              <th className="w-8 px-2 py-1.5" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const target: YTarget = row.target ?? 'maximize'
              return (
                <Fragment key={i}>
                  <tr className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                    <td className="px-1 py-1">
                      <input
                        type="text"
                        value={row.response}
                        onChange={(e) => update(i, { response: e.target.value })}
                        className="w-full rounded bg-bg-input px-2 py-1 text-[11px] text-text-body outline-none"
                        placeholder="e.g. f2"
                      />
                    </td>
                    <td className="px-1 py-1 text-center">
                      <select
                        value={target}
                        onChange={(e) => update(i, { target: e.target.value as YTarget })}
                        className="rounded bg-bg-input px-1 py-1 text-[11px] text-text-body outline-none"
                      >
                        {TARGETS.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-1 py-1">
                      <div className="flex items-center gap-1">
                        <span className="w-3 text-center text-text-secondary">{SYMBOL[target]}</span>
                        <input
                          type="number"
                          value={row.constraints}
                          onChange={(e) => update(i, { constraints: e.target.value })}
                          className="w-full rounded bg-bg-input px-2 py-1 text-[11px] text-text-body outline-none"
                          placeholder="e.g. 50"
                        />
                      </div>
                    </td>
                    <td className="px-2 py-1 text-center">
                      <button onClick={() => removeRow(i)} className="text-text-secondary hover:text-danger text-[10px]">
                        ✕
                      </button>
                    </td>
                  </tr>
                  {/* 용출액별 파생 행(BR-RND-05) — 원 f2 행 바로 아래, 읽기 전용. 설정은 원 행을 따른다. */}
                  {i === originIdx && derived.map((d) => (
                    <tr
                      key={d.response}
                      className="border-t"
                      style={{ borderColor: 'var(--border-subtle)' }}
                      data-testid="y-derived-row"
                      title="활성 Lot 의 용출액마다 자동으로 만들어지는 행입니다. 설정은 위 f2 행을 따릅니다(읽기 전용)."
                    >
                      <td className="py-1 pl-4 pr-2 text-text-secondary">
                        <span className="mr-1 opacity-60">↳</span>{d.response}
                      </td>
                      <td className="px-2 py-1 text-center text-text-secondary">
                        {TARGETS.find((t) => t.value === target)?.label ?? target}
                      </td>
                      <td className="px-2 py-1 text-text-secondary">
                        <span className="inline-block w-3 text-center">{SYMBOL[target]}</span>{' '}
                        {row.constraints !== '' && row.constraints != null ? row.constraints : '—'}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1 text-center text-[10px] text-text-muted">파생</td>
                    </tr>
                  ))}
                </Fragment>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-3 text-center text-text-secondary text-[11px]">
                  No responses defined. Click "+ Response" to add.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
